import React, { FC, memo, useCallback } from 'react';
import { Field, Formik } from 'formik';
import * as yup from 'yup';
import { InputField } from '../../../../components/InputField';
import Alert, { AlertType } from '../../../../components/Alert';
import ContainerWithLabel from '../../../../components/ContainerWithLabel';
import Space, { DirectionEnum, SpaceSizesEnum } from '../../../../components/Space/Space';
import styled from 'styled-components';
import { Button } from '../../../../components/Buttons';
import { up } from 'styled-breakpoints';
import { updateSelfInfo } from '../../../GiftBox/components/GifBoxForm';
import { SubTitle2 } from '../SuccessfulGift';
import { GiftThankYouState } from '../../types';

const FormContainer = styled.div`
  .header {
    text-align: center;
  }

  .cols {
    display: flex;
    justify-content: space-between;
    width: 100%;
  }

  .col {
    width: 48%;
  }

  ${up('md')} {
    width: 71.5%;
  }
`;

const PurchaseButton = styled(Button)`
  font-weight: normal;
  width: 100%;
  border-color: #f3f2f3;
  min-height: 50px;
  align-items: center;
`;

const ShowError = styled.div`
  padding: 0.8rem 0;
  color: ${({ theme }) => theme.colors.primaryDanger};
  font-size: 14px;
`;

type CreateAccountFormProps = {
  state: GiftThankYouState;
  updateState: (arg0: GiftThankYouState) => void;
};

const CreateAccountForm: FC<CreateAccountFormProps> = ({ state }) => {
  const initValues = {
    password: '',
    passwordConfirmation: '',
    success: false,
    error: '',
  };

  const validationSchema = yup.object().shape({
    password: yup
      .string()
      .required('Password is required')
      .min(10, 'Passwords must be at least 10 characters'),
    passwordConfirmation: yup.string().test('passwords-match', 'Passwords must match', function(value) {
      return this.parent.password === value;
    }),
  });

  const submitHandler = useCallback(async (values, actions) => {
    try {
      await updateSelfInfo({
        password: values.password,
        password_confirmation: values.passwordConfirmation,
      });
      actions.setFieldValue('success', true);
    } catch (e) {
      actions.setFieldValue('error', 'Unable to create the account.');
    }
  }, []);

  return (
    <FormContainer>
      <Formik
        key={JSON.stringify(state.user)}
        initialValues={initValues}
        onSubmit={submitHandler}
        validationSchema={validationSchema}
        enableReinitialize
      >
        {({ isValid, dirty, errors, values, handleSubmit, isSubmitting }) => (
          <>
            {values.success ? (
              <h3>Account successfully created!</h3>
            ) : (
              <Space direction={DirectionEnum.vertical} size={SpaceSizesEnum.medium}>
                <div className="header">
                  <SubTitle2>
                    Create a <strong>Dopple</strong> account for exclusive access and perks
                  </SubTitle2>
                </div>
                <span />
                <ContainerWithLabel label="Your Password" isBlack hasError={Boolean(errors.password)}>
                  <Field
                    name="password"
                    placeholder="Password"
                    value={values.password}
                    showError={errors.password}
                    component={InputField}
                    type="password"
                  />
                  {errors.password && <ShowError>{errors.password}</ShowError>}
                </ContainerWithLabel>
                <ContainerWithLabel
                  label="Confirm your password"
                  isBlack
                  hasError={Boolean(errors.passwordConfirmation)}
                >
                  <Field
                    name="passwordConfirmation"
                    placeholder="Confirm password"
                    value={values.passwordConfirmation}
                    showError={errors.passwordConfirmation}
                    component={InputField}
                    type="password"
                  />
                  {errors.passwordConfirmation && <ShowError>{errors.passwordConfirmation}</ShowError>}
                </ContainerWithLabel>
                {values.error && <Alert type={AlertType.ERROR}>{values.error}</Alert>}
                <PurchaseButton
                  type="submit"
                  disabled={!isValid || isSubmitting || !dirty}
                  onClick={() => handleSubmit(values)}
                  isLoading={isSubmitting}
                >
                  CREATE ACCOUNT
                </PurchaseButton>
              </Space>
            )}
          </>
        )}
      </Formik>
    </FormContainer>
  );
};

export default memo(CreateAccountForm);
